import React, { useEffect, useState } from 'react';
import {
  User, Mail, Phone, Globe, Clock, MapPin,
  Lock, Key, Shield, AlertTriangle, CheckCircle,
  Save, LogOut, RotateCcw
} from 'lucide-react';
import { usersAPI, authAPI } from '../services/api';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';
import { useAlert } from '../contexts/AlertContext';
import './AccountSettings.css';

const AccountSettings = ({ user, onUserUpdated }) => {
  const { showAlert } = useAlert();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    language: 'en',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    address: null,
    displayAddress: ''
  });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusType, setStatusType] = useState('success');
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phoneNumber || '',
        language: user.preferredLanguage || 'en',
        timeZone: user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        address: user.address || null,
        displayAddress: user.address?.formattedAddress || ''
      }));
    }
  }, [user]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      setStatusMessage(null);

      if ((formData.displayAddress || formData.address?.placeId) && !formData.address?.placeId) {
        setStatusType('error');
        setStatusMessage('Please select a full address from the suggestions so we can capture the location.');
        showAlert({
          title: 'Address required',
          message: 'Please select a full address from the suggestions so we can capture the location.',
          primaryLabel: 'OK'
        });
        return;
      }

      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phone,
        preferredLanguage: formData.language,
        timezone: formData.timeZone,
        address: formData.address?.placeId ? { placeId: formData.address.placeId } : null
      };

      const updatedUser = await usersAPI.updateProfile(payload);
      onUserUpdated?.(updatedUser);
      setFormData((prev) => ({
        ...prev,
        address: updatedUser?.address || prev.address,
        displayAddress: updatedUser?.address?.formattedAddress || prev.displayAddress
      }));
      setStatusType('success');
      setStatusMessage('Profile updated successfully');
    } catch (err) {
      console.error('Profile update failed:', err);
      setStatusType('error');
      setStatusMessage(err.message || 'Profile update failed');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setStatusType('error');
      setStatusMessage('Please fill in all password fields');
      showAlert({
        title: 'Password update',
        message: 'Please fill in all password fields',
        primaryLabel: 'OK'
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setStatusType('error');
      setStatusMessage('New passwords do not match');
      showAlert({
        title: 'Password update',
        message: 'New passwords do not match',
        primaryLabel: 'OK'
      });
      return;
    }

    try {
      setChangingPassword(true);
      setStatusMessage(null);

      await authAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setStatusType('success');
      setStatusMessage('Password updated successfully');
    } catch (err) {
      console.error('Password change failed:', err);
      setStatusType('error');
      const message = err.message || 'Failed to change password';
      setStatusMessage(message);
      showAlert({
        title: 'Password update failed',
        message,
        primaryLabel: 'OK'
      });
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="settings-shell">
      {statusMessage && (
        <div className="settings-message" data-type={statusType}>
          {statusType === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          {statusMessage}
        </div>
      )}

      {/* -- PROFILE INFO SECTION -- */}
      <section className="settings-section">
        <header className="settings-section-header">
          <h3><User /> Profile information</h3>
          <p>Update your personal details and contact preferences.</p>
        </header>
        <div className="settings-section-body">
          <div className="settings-form-grid">
            <label className="settings-form-group">
              <span><User /> First name</span>
              <input
                className="settings-input"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="e.g. John"
              />
            </label>
            <label className="settings-form-group">
              <span><User /> Last name</span>
              <input
                className="settings-input"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="e.g. Doe"
              />
            </label>
            <label className="settings-form-group">
              <span><Mail /> Email</span>
              <input
                className="settings-input"
                name="email"
                value={formData.email}
                disabled
              />
            </label>
            <label className="settings-form-group">
              <span><Phone /> Phone</span>
              <input
                className="settings-input"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1..."
              />
            </label>
            <label className="settings-form-group">
              <span><Globe /> Language</span>
              <select
                className="settings-select"
                name="language"
                value={formData.language}
                onChange={handleInputChange}
              >
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="es">Español</option>
              </select>
            </label>
            <label className="settings-form-group">
              <span><Clock /> Time zone</span>
              <input
                className="settings-input"
                name="timeZone"
                value={formData.timeZone}
                onChange={handleInputChange}
              />
            </label>
            <label className="settings-form-group full-width">
              <span><MapPin /> Address</span>
              <GooglePlacesAutocomplete
                placeholder="Search for your address..."
                className="settings-input" /* Pass class if the component supports it, otherwise wrap */
                value={formData.displayAddress || ''}
                onPlaceSelect={(address) =>
                  setFormData((prev) => ({
                    ...prev,
                    address: { placeId: address.placeId, formattedAddress: address.formattedAddress },
                    displayAddress: address.formattedAddress
                  }))
                }
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    address: null,
                    displayAddress: event.target.value
                  }))
                }
              />
            </label>
          </div>

          <div className="settings-control-bar">
            <button
              type="button"
              className="settings-control-button"
              data-variant="secondary"
              onClick={() => setFormData({ ...formData, phone: '', placeId: '', displayAddress: '' })}
            >
              <RotateCcw size={16} /> Reset
            </button>
            <button
              type="button"
              className="settings-control-button"
              data-variant="primary"
              onClick={handleSaveProfile}
              disabled={savingProfile}
            >
              <Save size={18} /> {savingProfile ? 'Saving...' : 'Save profile'}
            </button>
          </div>
        </div>
      </section>

      {/* -- PASSWORD SECTION -- */}
      <section className="settings-section">
        <header className="settings-section-header">
          <h3><Lock /> Password & security</h3>
          <p>Update your password and keep your account secure.</p>
        </header>
        <div className="settings-section-body">
          <div className="settings-form-grid">
            <label className="settings-form-group">
              <span><Key /> Current password</span>
              <input
                className="settings-input"
                type="password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={(event) => setPasswordData((prev) => ({ ...prev, currentPassword: event.target.value }))}
              />
            </label>
            <label className="settings-form-group">
              <span><Lock /> New password</span>
              <input
                className="settings-input"
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={(event) => setPasswordData((prev) => ({ ...prev, newPassword: event.target.value }))}
              />
            </label>
            <label className="settings-form-group">
              <span><CheckCircle /> Confirm new password</span>
              <input
                className="settings-input"
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={(event) => setPasswordData((prev) => ({ ...prev, confirmPassword: event.target.value }))}
              />
            </label>
          </div>

          <div className="settings-control-bar">
            <button
              type="button"
              className="settings-control-button"
              data-variant="secondary"
              onClick={() => setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })}
            >
              Clear fields
            </button>
            <button
              type="button"
              className="settings-control-button"
              data-variant="primary"
              onClick={handleChangePassword}
              disabled={changingPassword}
            >
              <Shield size={18} /> {changingPassword ? 'Updating...' : 'Update password'}
            </button>
          </div>
        </div>
      </section>

      {/* -- SESSION SECTION -- */}
      <section className="settings-section">
        <header className="settings-section-header">
          <h3><Shield /> Session & security</h3>
          <p>Manage active sessions and email verification.</p>
        </header>
        <div className="settings-section-body">
          <div className="settings-form-grid">
            <div className="settings-form-group">
              <span>Email verification</span>
              <div>
                <span className="settings-status-pill">
                  <CheckCircle size={14} /> {user?.emailVerified ? 'Verified' : 'Check your inbox'}
                </span >
              </div>
            </div>
          </div>

          <div className="settings-control-bar">
            <button
              type="button"
              className="settings-control-button"
              data-variant="secondary"
              onClick={() => authAPI.resendEmailVerification()}
            >
              <Mail size={16} /> Resend verification email
            </button>
            <button
              type="button"
              className="settings-control-button"
              data-variant="danger"
              onClick={() => authAPI.logout()}
            >
              <LogOut size={16} /> Sign out of all sessions
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccountSettings;
